const MODAL_TYPES = {
  REGISTRATION: 'registration',
  AUTH: 'auth',
  VERIFICATION: 'verification',
  AGE_CHECK: 'ageCheck',
} as const;

const URL_PARAMS = {
  MODAL: 'modal',
  RECOVERY_CODE: 'recoveryCode',
} as const;

interface AuthUrlInput {
  pathname: string;
  search: string;
}

interface GetAuthUrlStateInput extends AuthUrlInput {
  isPh: boolean;
}

interface GetAuthRedirectTargetInput extends AuthUrlInput {
  path: string;
}

const createSearchParams = (search: string) =>
  new URLSearchParams(search.startsWith('?') ? search.slice(1) : search);

const isAuthModalValue = (modalValue: string) =>
  modalValue === MODAL_TYPES.REGISTRATION ||
  modalValue === MODAL_TYPES.AUTH ||
  modalValue === MODAL_TYPES.VERIFICATION ||
  modalValue.startsWith(`${MODAL_TYPES.VERIFICATION}&`);

export const getAuthUrlState = ({
  pathname,
  search,
  isPh,
}: GetAuthUrlStateInput) => {
  const searchParams = createSearchParams(search);
  const modalParam = searchParams.get(URL_PARAMS.MODAL) ?? '';
  const isRegistration =
    pathname.endsWith(`/${MODAL_TYPES.REGISTRATION}`) ||
    modalParam === MODAL_TYPES.REGISTRATION;
  const isLogin =
    pathname.endsWith(`/${MODAL_TYPES.AUTH}`) ||
    modalParam === MODAL_TYPES.AUTH;
  const isRecoveryCode = searchParams.get(URL_PARAMS.RECOVERY_CODE);
  const isVerification =
    modalParam === MODAL_TYPES.VERIFICATION ||
    modalParam.startsWith(`${MODAL_TYPES.VERIFICATION}&`);
  const isAgeCheck = modalParam === MODAL_TYPES.AGE_CHECK && isPh;

  return {
    isRegistration,
    isLogin,
    isRecoveryCode,
    isVerification,
    isAgeCheck,
    should:
      Boolean(
        isRegistration || isLogin || Boolean(isRecoveryCode) || isVerification
      ) && !isAgeCheck,
  };
};

export const buildAuthModalCloseUrl = ({ pathname, search }: AuthUrlInput) => {
  const pathPattern = new RegExp(
    `\\/(${MODAL_TYPES.REGISTRATION}|${MODAL_TYPES.AUTH})$`
  );
  const cleanPathname = pathname.replace(pathPattern, '');
  const searchParams = createSearchParams(search);
  const modalValue = searchParams.get(URL_PARAMS.MODAL) ?? '';

  if (isAuthModalValue(modalValue)) {
    searchParams.delete(URL_PARAMS.MODAL);
  }

  searchParams.delete(URL_PARAMS.RECOVERY_CODE);

  const queryString = searchParams.toString();

  return cleanPathname + (queryString ? `?${queryString}` : '');
};

export const getAuthRedirectTarget = ({
  pathname,
  search,
  path,
}: GetAuthRedirectTargetInput) => {
  const modalParam = createSearchParams(search).get(URL_PARAMS.MODAL);

  if (
    modalParam === MODAL_TYPES.AUTH ||
    modalParam === MODAL_TYPES.REGISTRATION
  ) {
    return `?${URL_PARAMS.MODAL}=${path}`;
  }

  if (pathname.endsWith(`/${path}`)) return null;

  return `/${path}`;
};
